import { getName, getAlpha2Code } from "i18n-iso-countries";
import { Country } from "../../shared/types/country.type";
import { LocaleLanguages } from "../types";
import nationalities from "i18n-nationality";
import enLocale from "i18n-nationality/langs/en.json";
nationalities.registerLocale(enLocale);

/**
 * Converts an array of alpha-2 country codes to an array of objects containing the country codes and their corresponding names in the specified locale.
 *
 * @param alpha2Codes - An array of alpha-2 country codes (e.g., ["US", "FR", "DE"]).
 * @param userLocale - The locale to use for the country names (default is "en").
 * @returns An array of objects, each containing an alpha-2 country code and its corresponding name in the specified locale. If no valid country names are found, an empty array is returned.
 */
export const alpha2CodesToCountryNames = (
  alpha2Codes: string[],
  userLocale = "en"
) => {
  const langCode = `${userLocale.substring(0, 2)}`;

  if (alpha2Codes.length != 0 && langCode != undefined) {
    const CountryLists: Array<Country> = [];
    alpha2Codes.forEach((countryCode) => {
      if (getName(countryCode, langCode) !== undefined) {
        CountryLists.push({
          alpha2Code: countryCode,
          name: getName(countryCode, langCode) ?? "",
        });
      }
    });
    return CountryLists;
  } else {
    return [];
  }
};

/**
 * Retrieves the name of a country based on its country code.
 *
 * @param countryCode - The country code to look up. Can be a string or null.
 * @param displayHyphen - A boolean indicating whether to display a hyphen if the country code is null or undefined. Defaults to true.
 * @param userLocale - The locale to use for the language code. Defaults to "en-GB".
 * @returns The name of the country corresponding to the given country code, or a hyphen/empty string based on the displayHyphen flag.
 */
export const getNameByCountryCode = (
  countryCode?: string | null,
  displayHyphen = true,
  userLocale: LocaleLanguages = "en-GB"
): string => {
  const langCode = `${userLocale.substring(0, 2)}`;

  if (countryCode !== null && countryCode !== undefined) {
    return getName(countryCode, langCode) ?? "";
  } else if (displayHyphen) {
    return "-";
  } else {
    return "";
  }
};

/**
 * Retrieves the country code based on the provided nationality.
 *
 * @param nationality - The nationality to get the country code for. Can be a string or null.
 * @param displayHyphen - A boolean indicating whether to display a hyphen if the country code is not found. Defaults to true.
 * @param userLocale - The locale to use for language-specific country code lookup. Defaults to "en-GB".
 * @returns The country code as a string. If the nationality is not found and displayHyphen is true, returns "-". Otherwise, returns an empty string.
 */
export const getCountryCodeByNationality = (
  nationality: string | null,
  displayHyphen = true,
  userLocale: LocaleLanguages = "en-GB"
): string => {
  const langCode = `${userLocale.substring(0, 2)}`;

  if (nationality !== null && nationality !== undefined) {
    if (nationality.length == 2) return nationality;
    return (
      nationalities.getAlpha2Code(nationality, langCode) ??
      (displayHyphen ? "-" : "")
    );
  } else if (displayHyphen) {
    return "-";
  } else {
    return "";
  }
};

/**
 * Retrieves the country code (ISO Alpha-2) based on the provided country name.
 *
 * @param countryName - The name of the country for which to retrieve the code. Can be null.
 * @param displayHyphen - A boolean indicating whether to display a hyphen ("-") if the country code is not found. Defaults to true.
 * @param userLocale - The locale to use for language-specific country names. Defaults to "en-GB".
 * @returns The ISO Alpha-2 country code if found, otherwise a hyphen ("-") or an empty string based on the displayHyphen parameter.
 */
export const getCountryCodeByCountryName = (
  countryName: string | null,
  displayHyphen = true,
  userLocale: LocaleLanguages = "en-GB"
): string => {
  const langCode = `${userLocale.substring(0, 2)}`;

  if (countryName !== null && countryName !== undefined) {
    return getAlpha2Code(countryName, langCode) ?? (displayHyphen ? "-" : "");
  } else if (displayHyphen) {
    return "-";
  } else {
    return "";
  }
};
